import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { connectConversationWS } from '../services/aiWebSocket';

const API = process.env.REACT_APP_AI_API_URL || '/api/ai';

interface Conversation { id: string; customer_id: string; customer_name?: string; channel: string; status: string; created_at: string; }
interface Message { id: string; sender_type: string; message: string; timestamp: string; confidence_score?: number; escalation_triggered?: boolean; }

const STATUS_COLORS: Record<string, string> = {
  AI_ACTIVE: '#10b981', WAITING_AGENT: '#f59e0b', AGENT_ASSIGNED: '#6366f1', CLOSED: '#64748b',
};
const SENDER_COLORS: Record<string, string> = {
  CUSTOMER: '#e2e8f0', AI: '#ede9fe', AGENT: '#d1fae5', SYSTEM: '#fef3c7',
};

export default function AIConversations() {
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const wsRef = useRef<ReturnType<typeof connectConversationWS> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const token = localStorage.getItem('token');

  const loadConversations = useCallback(async () => {
    const params: Record<string, string> = { page: String(page), page_size: '20' };
    if (statusFilter) params.status = statusFilter;
    const r = await axios.get(`${API}/conversations`, { params, headers: { Authorization: `Bearer ${token}` } });
    setConvs(r.data.items || []);
    setTotal(r.data.total || 0);
  }, [page, statusFilter, token]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const openConversation = async (conv: Conversation) => {
    setSelected(conv);
    setMessages([]);
    wsRef.current?.disconnect();

    const r = await axios.get(`${API}/conversations/${conv.id}/messages`, { headers: { Authorization: `Bearer ${token}` } });
    setMessages(r.data || []);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

    const ws = connectConversationWS(conv.id, token, 'agent', 'agent');
    wsRef.current = ws;
    ws.on('ai_message', (d) => {
      setMessages(m => [...m, { id: String(d.message_id), sender_type: 'AI', message: String(d.message), timestamp: String(d.timestamp), confidence_score: d.confidence as number }]);
      setTyping(false);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    ws.on('agent_message', (d) => {
      setMessages(m => [...m, { id: String(d.message_id), sender_type: 'AGENT', message: String(d.message), timestamp: String(d.timestamp) }]);
    });
    ws.on('typing', () => { setTyping(true); setTimeout(() => setTyping(false), 3000); });
    ws.on('agent_joined', () => loadConversations());
    ws.on('returned_to_ai', () => loadConversations());
  };

  const sendAgentMessage = async () => {
    if (!selected || !input.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/agent/message`, { conversation_id: selected.id, message: input }, { headers: { Authorization: `Bearer ${token}` } });
      setInput('');
    } finally { setSending(false); }
  };

  const handleTakeover = async () => {
    if (!selected) return;
    await axios.post(`${API}/agent/takeover`, { conversation_id: selected.id }, { headers: { Authorization: `Bearer ${token}` } });
    loadConversations();
  };

  const handleRelease = async () => {
    if (!selected) return;
    await axios.post(`${API}/agent/release`, { conversation_id: selected.id }, { headers: { Authorization: `Bearer ${token}` } });
    loadConversations();
  };

  const handleClose = async () => {
    if (!selected) return;
    await axios.post(`${API}/conversations/${selected.id}/close`, {}, { headers: { Authorization: `Bearer ${token}` } });
    loadConversations();
  };

  useEffect(() => { return () => wsRef.current?.disconnect(); }, []);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', background: '#0f172a', color: '#e2e8f0' }}>
      {/* Sidebar */}
      <div style={{ width: 320, borderRight: '1px solid #1e293b', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid #1e293b' }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>AI Conversations</div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: '100%', background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: 13 }}>
            <option value="">All Statuses</option>
            {['AI_ACTIVE','WAITING_AGENT','AGENT_ASSIGNED','CLOSED'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {convs.map(c => (
            <div key={c.id} onClick={() => openConversation(c)}
              style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #1e293b', background: selected?.id === c.id ? '#1e293b' : 'transparent', transition: 'background 0.15s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{c.customer_name || c.customer_id}</div>
                <span style={{ fontSize: 10, background: STATUS_COLORS[c.status] + '22', color: STATUS_COLORS[c.status], border: `1px solid ${STATUS_COLORS[c.status]}44`, borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>{c.status.replace('_',' ')}</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                📱 {c.channel} · {new Date(c.created_at).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid #1e293b', display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={btnStyle}>← Prev</button>
          <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>{page} / {Math.ceil(total / 20) || 1}</span>
          <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} style={btnStyle}>Next →</button>
        </div>
      </div>

      {/* Chat panel */}
      {selected ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{selected.customer_name || selected.customer_id}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{selected.channel} · {selected.id.slice(0, 8)}</div>
            </div>
            <span style={{ fontSize: 11, background: STATUS_COLORS[selected.status] + '22', color: STATUS_COLORS[selected.status], borderRadius: 4, padding: '3px 8px', fontWeight: 700, border: `1px solid ${STATUS_COLORS[selected.status]}44` }}>
              {selected.status.replace('_',' ')}
            </span>
            {selected.status === 'WAITING_AGENT' && (
              <button onClick={handleTakeover} style={{ ...btnPrimary, background: '#6366f1' }}>Take Over</button>
            )}
            {selected.status === 'AGENT_ASSIGNED' && (
              <button onClick={handleRelease} style={{ ...btnPrimary, background: '#f59e0b' }}>Release to AI</button>
            )}
            {selected.status !== 'CLOSED' && (
              <button onClick={handleClose} style={{ ...btnPrimary, background: '#ef4444' }}>Close</button>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: m.sender_type === 'CUSTOMER' ? 'flex-start' : 'flex-end' }}>
                <div style={{ maxWidth: '72%' }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3, textAlign: m.sender_type === 'CUSTOMER' ? 'left' : 'right' }}>
                    {m.sender_type} · {new Date(m.timestamp).toLocaleTimeString()}
                    {m.confidence_score !== undefined && ` · ${(m.confidence_score * 100).toFixed(0)}% conf`}
                  </div>
                  <div style={{ background: SENDER_COLORS[m.sender_type] || '#1e293b', color: '#0f172a', borderRadius: 10, padding: '8px 12px', fontSize: 14, lineHeight: 1.5 }}>
                    {m.message}
                  </div>
                  {m.escalation_triggered && (
                    <div style={{ marginTop: 4, fontSize: 10, color: '#f59e0b', textAlign: 'right' }}>⚠ Escalation triggered</div>
                  )}
                </div>
              </div>
            ))}
            {typing && (
              <div style={{ alignSelf: 'flex-end', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>AI is typing…</div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          {selected.status === 'AGENT_ASSIGNED' && (
            <div style={{ padding: 16, borderTop: '1px solid #1e293b', display: 'flex', gap: 10 }}>
              <input
                value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendAgentMessage()}
                placeholder="Reply as agent…"
                style={{ flex: 1, background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 8, padding: '10px 14px', fontSize: 14 }}
              />
              <button onClick={sendAgentMessage} disabled={sending || !input.trim()} style={btnPrimary}>
                {sending ? '…' : 'Send'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155', fontSize: 16 }}>
          Select a conversation to view
        </div>
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { background: '#8b5cf6', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
